import React from 'react';

// Components
import FormattedMessageV2, { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { TaxAnalysisComparisonItemsSelector, TaxAnalysisInputsContainer, TaxAnalysisDataTable } from './components';

// Hooks
import { useTaxAnalysisInputs, useTaxAnalysisData } from './hooks';

// Libraries
import { useSelector } from 'react-redux';

// Utils
import { portfolioHasMissingCostBasis } from '@/utils/portfolios';

// Types
import ComparisonItem from '@/types/comparison-item';
import { formatMessage, formatMessageWithLocale } from '@/utils/i18n';
import { GenericEmptyState } from '@/views/components/misc';
import { ReduxState } from '@/redux/modules/types';
import { Typography } from '@capintel/component-lib/typography';

interface TaxAnalysisViewProps {
  availableComparisonItems: ComparisonItem[];
  onEditItem: (item: ComparisonItem, forceShowTaxDetails?: boolean) => void;
  onCreatePortfolio: () => void;
  readOnly?: boolean;
}

/**
 * TaxAnalysisView component integrates the Calculator library to display tax analysis.
 * Uses the same pattern as the portfolio editor builder for consistency.
 */
const TaxAnalysisView: React.FC<TaxAnalysisViewProps> = ({ availableComparisonItems, onEditItem, onCreatePortfolio, readOnly = false }) => {
  const emptyComparison = !availableComparisonItems || availableComparisonItems.length === 0;

  if (emptyComparison) {
    return (
      <GenericEmptyState
        header={formatMessage('Comparison.TaxAnalysis.EmptyState.EmptyComparison.Header')}
        body={
          <div className="empty-comparison-state-body">
            <span>{formatMessage('Comparison.TaxAnalysis.EmptyState.EmptyComparison.Body')}</span>
            {!readOnly && <span>{formatMessage('Comparison.TaxAnalysis.EmptyState.EmptyComparison.ActionInstruction')}</span>}
          </div>
        }
        actionLabel={formatMessage('Comparison.TaxAnalysis.EmptyState.EmptyComparison.ActionLabel')}
        onAction={onCreatePortfolio}
        disableAction={readOnly}
      />
    );
  }

  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const isPortfolioEditorOpen = useSelector((state: ReduxState) => state.comparison.portfolioEditor.isVisible);
  const locale = useSelector((state: ReduxState) => state.intl.locale);

  const originalComparisonItemId = activeComparison?.taxAnalysis?.originalComparisonItemId;
  const targetComparisonItemId = activeComparison?.taxAnalysis?.targetComparisonItemId;

  // Get selected items from Redux based on stored IDs
  const selectedItemsFromRedux = React.useMemo(() => {
    if (!activeComparison?.items) return [];

    return [originalComparisonItemId, targetComparisonItemId]
      .filter(Boolean)
      .map(id => activeComparison.items.find(item => item._id === id))
      .filter((item): item is ComparisonItem => item !== undefined);
  }, [originalComparisonItemId, targetComparisonItemId, activeComparison?.items]);

  const emptySelectedItems = selectedItemsFromRedux.length === 0;

  const taxInputs = useTaxAnalysisInputs();

  // Destructure first item as primary portfolio, second as target (if available)
  const [selectedComparisonItem, selectedTargetComparisonItem] = selectedItemsFromRedux;

  useTaxAnalysisData({
    selectedItem: selectedComparisonItem,
    regionInputs: taxInputs.regionInputs,
    targetComparisonItem: selectedTargetComparisonItem,
    isPortfolioEditorOpen,
    readOnly,
  });

  const holdings = selectedComparisonItem?.holdings || [];
  const isOriginalPortfolioMissingCostBasis = portfolioHasMissingCostBasis(holdings);
  const hasTargetPortfolio = !!selectedTargetComparisonItem;

  return (
    <div className="tax-analysis-view">
      <div className="tax-analysis-section">
        <div className="tax-analysis-section-header">
          <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.H3} toLocalize="Comparison.TaxAnalysis.Title" />
          <TaxAnalysisInputsContainer readOnly={readOnly} taxInputs={taxInputs} />
          <TaxAnalysisComparisonItemsSelector availableComparisonItems={availableComparisonItems} onEditItem={onEditItem} readOnly={readOnly} />
        </div>

        {emptySelectedItems && (
          <div className="table-empty-portfolio">
            <GenericEmptyState
              header={formatMessage('Comparison.TaxAnalysis.EmptyState.NoSelectedPortfolios.Header')}
              body={formatMessage('Comparison.TaxAnalysis.EmptyState.NoSelectedPortfolios.Body')}
            />
          </div>
        )}

        {!emptySelectedItems && isOriginalPortfolioMissingCostBasis && (
          <div className="table-empty-portfolio">
            <GenericEmptyState
              header={formatMessage('Comparison.TaxAnalysis.EmptyState.MissingCostBasis.Header')}
              body={formatMessage('Comparison.TaxAnalysis.EmptyState.MissingCostBasis.Body')}
              actionLabel={!readOnly && formatMessage('Comparison.TaxAnalysis.EmptyState.MissingCostBasis.ActionLabel')}
              onAction={selectedComparisonItem ? () => onEditItem(selectedComparisonItem, true) : undefined}
              disableAction={readOnly}
            />
          </div>
        )}

        {!emptySelectedItems && !isOriginalPortfolioMissingCostBasis && (
          <>
            <TaxAnalysisDataTable readOnly={readOnly} hasTargetPortfolio={hasTargetPortfolio} />
            <div className="tax-analysis-footer">
              <Typography as="p" variant="bodySmRegular">
                {formatMessageWithLocale('Comparison.TaxAnalysis.Disclaimer', locale)}
              </Typography>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TaxAnalysisView;
